import React, { KeyboardEvent, ReactElement, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Date } from '../../../../../common/src/calendar.ts';
import {
  addDayOffset,
  dateFromString,
  DateRange,
  dateRangeOverlapsDate,
  DateString,
  dateStringFromDate,
  daysBetween,
  getDateAsShortString,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthName,
  maxDate,
  minDate,
  newestDate,
  todayDate,
} from '../../../../../common/src/date_string.ts';
import {
  ClientOrg,
  HymnOfTheMonthSongList,
  IntervalSongList,
  IntervalType,
  Org,
  OrgType,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
} from '../../../../../common/src/model.ts';
import {
  getSongListEndDate,
  getSongListStartDate,
  getWeeklySongListType,
  isBound,
  isWeeklySongList,
} from '../../../../../common/src/model_util.ts';
import { getSongEntriesForDate, getWeeklyDateForOrganization } from '../../../../../common/src/org.ts';
import { Pages } from '../../../../../common/src/pages.ts';
import { getFillStartSunday } from '../../../../../common/src/passed_sunday_clock.ts';
import {
  getRepeatFrom,
  getRepeatKind,
  getWeeklySongEntriesForDate,
  isRunKind,
} from '../../../../../common/src/repeating_rows.ts';
import { NonEmptyArray } from '../../../../../common/src/util.ts';
import { appendVersesSuffix } from '../../../../../common/src/verses.ts';
import DashboardIcon from '../../../assets/dashboard.svg?react';
import { localStorageGet, LocalStorageKey } from '../../../data/client_local_storage.ts';
import { useWrittenThroughForOrg } from '../../../data/passed_sundays_written_through.ts';
import {
  refreshSingingPlans,
  songFutureSizeInWeeks,
  synchronizeAllSongLists,
  synchronizeOrgsWithServer,
  useCurrentOrg,
} from '../../../data/use_current_org.tsx';
import { useCustomMusic } from '../../../data/use_custom_music.tsx';
import { HymnalsContext } from '../../../data/use_hymnals.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { confirm } from '../../../util/confirm.tsx';
import { goBack } from '../../../util/go_back.ts';
import { getDisplayNameForSongListEntry, getUrlForPage } from '../../../util/path.ts';
import { getPlatform, Platform } from '../../../util/platform.ts';
import { getTermForDateRange, getTermWeekNumber } from '../../../util/school.ts';
import { OuterPageContent } from '../../../util/shared.tsx';
import { Spinner } from '../../../util/spinner.tsx';
import { SyncAnimation } from '../../../util/sync_animation.tsx';
import {
  HeaderButtonBorderRadius,
  HeaderButtonPaddingDesktop,
  UploadSongButton,
} from '../../../util/upload_song_button.tsx';
import { useCurrentDate, useReaderInstant } from '../../../util/use_current_date.tsx';

import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { useHoldAppReload } from '../../../util/use_hold_app_reload.ts';
import { DownloadSongSheetsButton } from './download_song_sheets.tsx';
import { entryFromPastedText } from './paste_entry.ts';
import { applyColumnClear, applyPaste, applyPlannerEdit, getCellEdit, PlannerWriteContext } from './planner_edits.ts';
import { PlannerSchemaColumn } from './planner_schema_column.tsx';
import { RepeatGlyphs } from './repeat_kind_ui.tsx';
import {
  ClearLabel,
  getClearLabel,
  getRepeatingRowCellsByRow,
  getSelectionReach,
  getWeeklyListsByDate,
  isRepeatingRow,
  RepeatingRowBlankMark,
} from './repeating_row_cells.ts';
import {
  BackButton,
  BackButtonPlaceholder,
  BackButtonWidth,
  DateRowTop,
  MinSongListRowCount,
  PlannerButtonIconThreshold,
  PlannerHeaderButtonGap,
  PlannerYearHeight,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
  YearHeader,
} from './shared.tsx';
import { CellHoverOutlineWidth, isClearKey, SelectCellOptions, SongCell } from './song_cell.tsx';
import { getEmptyWeeklySongList } from './song_list_rows.ts';
import { PlannerSaveRefusalBanner } from './song_list_save_refusal_banner.tsx';
import { EditTermsButton, openTermPlanner } from './term_planner.tsx';
import { TermSelector } from './term_selector.tsx';
import { AllSongLists, getSchoolSongListSchema, useAllSongLists } from './use_all_song_lists.ts';
import {
  getScrollLeftToCenterColumn,
  getScrollLeftToShowColumn,
  useColumnVirtualizer,
} from './use_column_virtualizer.ts';
import { usePlannerKeyboardNavigation } from './use_planner_keyboard_navigation.tsx';

const schoolPlannerBeginEndPaddingWeeks = 2;
// the cell menu offers Paste only where the page can read the clipboard (Firefox before 125 cannot)
const canReadClipboard = typeof navigator.clipboard?.readText === 'function';
const minSchoolStartDate = addDayOffset(todayDate(), -2 * 365);
const maxSchoolEndDate = addDayOffset(todayDate(), 2 * 365);

export interface CellPosition {
  row: number; // 1 based
  column: number; // 0 based
}

export const Planner = () => {
  const [syncComplete, setSyncComplete] = useState(false);
  const navigate = useNavigate();

  useEffectOnce(() => {
    void (async () => {
      await synchronizeAllSongLists();
      setSyncComplete(true);
    })();
  });

  if (!syncComplete) {
    return (
      <OuterPageContent>
        <LoadingHeader>
          <BackButton onClick={() => void goBack(navigate)} />
          <YearHeader>{`${new window.Date().getFullYear()} Liturgy Planner`}</YearHeader>
        </LoadingHeader>
        <LoadingSpinnerWrapper>
          <Spinner />
        </LoadingSpinnerWrapper>
      </OuterPageContent>
    );
  }

  return <PlannerContent />;
};

const LoadingHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 10px 10px calc(${BackButtonWidth} + ${PlannerHeaderButtonGap});
`;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const PlannerContent = () => {
  const allSongLists = useAllSongLists();
  const navigate = useNavigate();
  const { customMusic } = useCustomMusic();
  const hymnals = use(HymnalsContext);
  const { currentOrg } = useCurrentOrg();
  // a cell follows the church-only rules (blanks, issues #4190 and #4200) only for a church: before the org loads it
  // behaves as a school's does, as every cell did before those rules
  const isChurch = currentOrg?.type === OrgType.Church;
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const today = useCurrentDate();
  const getReaderNow = useReaderInstant();
  const currentWeekDate = getWeeklyDateForOrganization(currentOrg, today);
  // how far the server had written the church's passed Sundays down when the planner's lists downloaded
  const writtenThrough = useWrittenThroughForOrg(currentOrg);
  // the first Sunday a repeating row fills on screen: every passed Sunday after `writtenThrough` goes on filling until
  // the server has written it down (issue #4192). Without `writtenThrough` (never heard from the server), the Sunday
  // before the current one fills until Monday noon UTC: read at each render's instant (a string, so the memos below
  // hold until it moves), that window closes on the first re-render or date change after Monday noon UTC. An injected
  // date (a story, a test) reads at its local noon, so its window does not depend on when it runs.
  const fillStartDate = getFillStartSunday(getReaderNow(), currentWeekDate, writtenThrough);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const schema =
    currentOrg?.type === OrgType.School
      ? getSchoolSongListSchema(currentOrg, allSongLists)
      : currentOrg?.churchLiturgySchema;
  const selectedWeekRef = useRef<HTMLDivElement>(null);
  const [schoolTerms] = useLocalStorage(LocalStorageKey.SchoolTerms);

  const hymnsOfTheMonth = useMemo(
    () =>
      new Map<DateString, HymnOfTheMonthSongList>(
        (allSongLists.filter(({ type }) => type === SongListType.HymnOfTheMonth) as HymnOfTheMonthSongList[]).map(
          (songList) => [getSongListStartDate(songList), songList],
        ),
      ),
    [allSongLists],
  );
  const monthlySongLists = useMemo(
    () =>
      new Map<DateString, IntervalSongList>(
        (
          allSongLists.filter(
            (list) => list.type === SongListType.Interval && list.interval.type === IntervalType.Month,
          ) as IntervalSongList[]
        ).map((list) => [getSongListStartDate(list), list]),
      ),
    [allSongLists],
  );
  const weeklySongLists = useMemo(
    () =>
      new Map<DateString, SongList>(
        allSongLists
          .filter((songList) => isWeeklySongList(currentOrg, songList))
          .map((songList) => {
            const date = getSongListStartDate(songList);
            return [date, songList];
          }),
      ),
    [allSongLists, currentOrg],
  );

  const onBack = useCallback(() => {
    void (async () => {
      await synchronizeAllSongLists();
      if (currentOrg) {
        await refreshSingingPlans(currentOrg.id);
        await synchronizeOrgsWithServer({ force: true });
      }
      await goBack(navigate);
    })();
  }, [currentOrg, navigate]);

  const oldestPlannerDate = useMemo(
    () => getOldestPlannerDate(currentOrg, schoolTerms, allSongLists, currentWeekDate),
    [allSongLists, currentOrg, currentWeekDate, schoolTerms],
  );

  const newestPlannerDate = useMemo(() => {
    return getNewestPlannerDate(currentOrg, schoolTerms, allSongLists, currentWeekDate);
  }, [allSongLists, currentOrg, currentWeekDate, schoolTerms]);

  const { weeklyDates, columnsInYear, termHeaderInfo, columnsInMonth, datesOutsideSchoolTerm } = useMemo(() => {
    const weekCount = Math.round(daysBetween(oldestPlannerDate, newestPlannerDate) / 7) + 1;
    const columnsInYear: { [year: string]: number } = {};
    const termHeaderInfo: Array<{
      startDate: string;
      term: IntervalSongList | undefined;
      columnStart: number;
      columnCount: number;
    }> = [];
    const columnsInMonth = new Map<DateString, { columnStart: number; columnCount: number }>(); // DateString = first day of the month
    const weeklyDates: DateString[] = [];
    const date = dateFromString(oldestPlannerDate);
    const datesOutsideSchoolTerm = new Set<DateString>();
    for (let index = 0; index < weekCount; index++) {
      const weeklyDate = dateStringFromDate(date);
      weeklyDates.push(weeklyDate);
      columnsInYear[date.getFullYear()] = (columnsInYear[date.getFullYear().toString()] ?? 0) + 1;
      const firstDayOfMonth = getFirstDayOfMonth(weeklyDate);
      const currentMonthlyEntry = columnsInMonth.get(firstDayOfMonth);
      columnsInMonth.set(firstDayOfMonth, {
        columnStart: currentMonthlyEntry?.columnStart ?? index,
        columnCount: (currentMonthlyEntry?.columnCount ?? 0) + 1,
      });
      if (currentOrg?.type === OrgType.School) {
        const term = getTermForDateRange({ start: weeklyDate, end: addDayOffset(weeklyDate, 6) }, schoolTerms);
        const isDateOutsideSchoolTerms = currentOrg?.type === OrgType.School && !term;
        if (isDateOutsideSchoolTerms) {
          datesOutsideSchoolTerm.add(weeklyDate);
        }
        if (termHeaderInfo.length === 0 || term !== termHeaderInfo[termHeaderInfo.length - 1].term) {
          termHeaderInfo.push({ startDate: weeklyDate, term, columnStart: index, columnCount: 1 });
        } else {
          termHeaderInfo[termHeaderInfo.length - 1].columnCount += 1;
        }
      }
      date.setDate(date.getDate() + 7);
    }
    return {
      weeklyDates: weeklyDates as NonEmptyArray<DateString>,
      columnsInYear,
      termHeaderInfo,
      columnsInMonth,
      datesOutsideSchoolTerm,
    };
  }, [currentOrg?.type, newestPlannerDate, oldestPlannerDate, schoolTerms]);

  // the stored array, which keeps its identity until the lists change, unlike the hook's object
  const songLists = allSongLists.toArray();
  const { update, updateEach } = allSongLists;

  // the cells of each church row that repeats (issue #4191), filled from the row's own Sundays: runs of the same song
  // for Same every Sunday and Same all month, one cell per Sunday for By Sunday of the month. Built once per render
  // per row; a school has no repeating row.
  const repeatingRowCells = useMemo(
    () =>
      getRepeatingRowCellsByRow({
        org: currentOrg,
        rows: schema?.rows ?? [],
        songLists,
        weeklyDates,
        currentWeekDate,
        fillStartDate,
      }),
    [currentOrg, currentWeekDate, fillStartDate, schema?.rows, songLists, weeklyDates],
  );

  // the org's weekly lists by Sunday, the first for a Sunday as a repeating row's write finds it, so the cell menu's
  // Clear decision sees the list the write would change
  const weeklyListsByDate = useMemo(() => getWeeklyListsByDate(currentOrg, songLists), [currentOrg, songLists]);

  // a church's weekly cell on the Sunday `date`, a repeating row's or a plain row's, as its write and its Clear
  // decision take it: the Sunday, the row's repeat and the fill as drawn. By date, not column, so an edit open while the
  // planner's columns shift still names its Sunday (#4201)
  const getPlannerCellAt = useCallback(
    (rowIndex: number, date: DateString) => {
      const row = schema?.rows.find((schemaRow) => schemaRow.row === rowIndex);
      return {
        org: currentOrg,
        kind: getRepeatKind(row),
        repeatFrom: getRepeatFrom(row),
        rowIndex,
        date,
        currentWeekDate,
        fillStartDate,
      };
    },
    [currentOrg, currentWeekDate, fillStartDate, schema?.rows],
  );

  // What an edit of the org's weekly lists from the grid (a church's cell entry or Clear, a paste, a column clear) is
  // decided on and written through. A church's edit is written in one updateEach call, which saves each list and
  // deletes the emptied ones in turn, nothing when the edit changes nothing; one made while a passed Sunday is not yet
  // written down writes it down first, in the same call, so the edit stands (applyPlannerEdit, issue #4209). A school's
  // or small group's paste and column clear are written exactly as before (applyPaste, applyColumnClear)
  const plannerWriteContext = useMemo(
    (): PlannerWriteContext => ({ org: currentOrg, songLists, update, updateEach, currentWeekDate, fillStartDate }),
    [currentOrg, currentWeekDate, fillStartDate, songLists, update, updateEach],
  );

  // an edit of a church's weekly cell, a repeating row's or a plain row's, writes the weekly list of the edited Sunday,
  // and for an entry in a Same-all-month row from the current Sunday on also the later Sundays of its month it takes
  // the row's own entries off (issue #4210). The fill does the rest
  const updateCellEntry = useCallback(
    ({ rowIndex, date, entry }: { rowIndex: number; date: DateString; entry: SongListEntry | undefined }) =>
      void applyPlannerEdit(plannerWriteContext, getCellEdit({ ...getPlannerCellAt(rowIndex, date), entry })),
    [getPlannerCellAt, plannerWriteContext],
  );

  // the menu offers Clear on a repeating row's cell exactly where Clear writes, named from what it does there: the same
  // decision as the write (getClearEffect, issues #4200, #4209, #4211), given what the cell drawn there shows
  // (`shownEntry`, the repeatingRowCells cell's entry), without building the list or resolving the fill again, since
  // every visible cell asks on every render
  const getRepeatingCellClearLabel = useCallback(
    (rowIndex: number, date: DateString, shownEntry: SongListEntry | undefined) =>
      getClearLabel({ ...getPlannerCellAt(rowIndex, date), weeklyList: weeklyListsByDate.get(date), shownEntry }),
    [getPlannerCellAt, weeklyListsByDate],
  );

  // Column virtualization: only render columns visible in the viewport + buffer
  // the numeric mirror of the CSS SongRowWidth, min(calc(100vw - 4px), 300px), which sizes the columns on screen
  const columnWidth = Math.min(window.innerWidth - 4, 300);
  // the row labels, one column wide, stick to the grid's left edge; a phone shows them in each cell instead
  const isLabelColumnRendered = getPlatform() !== Platform.Mobile;
  const labelColumnWidth = isLabelColumnRendered ? columnWidth : 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(() => {
    const idx = weeklyDates.indexOf(currentWeekDate);
    const target = idx !== -1 ? idx : weeklyDates.length - 1;
    return getScrollLeftToCenterColumn({ column: target, columnWidth, labelColumnWidth, viewWidth: window.innerWidth });
  });
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  const { firstVisibleIndex, lastVisibleIndex } = useColumnVirtualizer({
    scrollLeft,
    containerWidth,
    columnWidth,
    totalColumnCount: weeklyDates.length,
  });

  const rafRef = useRef<number | undefined>(undefined);
  const onScroll = useCallback(() => {
    if (rafRef.current !== undefined) {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el) {
        setScrollLeft(el.scrollLeft);
        setContainerWidth(el.clientWidth);
      }
      rafRef.current = undefined;
    });
  }, []);

  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | undefined>();
  const selectedColumnDate =
    selectedColumnIndex !== undefined && selectedColumnIndex < weeklyDates.length
      ? weeklyDates[selectedColumnIndex]
      : undefined;

  useEffectOnce(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const idx = weeklyDates.indexOf(currentWeekDate);
      const target = idx !== -1 ? idx : weeklyDates.length - 1;
      container.scrollLeft = getScrollLeftToCenterColumn({
        column: target,
        columnWidth,
        labelColumnWidth,
        viewWidth: container.clientWidth,
      });
    }
  });

  function getDayElementId(date: DateString): string {
    return `planner-day-${date}`;
  }

  const isColumnVisible = useCallback(
    (columnIndex: number) => columnIndex >= firstVisibleIndex && columnIndex <= lastVisibleIndex,
    [firstVisibleIndex, lastVisibleIndex],
  );

  const isColumnRangeVisible = useCallback(
    (columnStart: number, columnCount: number) => {
      const columnEnd = columnStart + columnCount - 1;
      return columnEnd >= firstVisibleIndex && columnStart <= lastVisibleIndex;
    },
    [firstVisibleIndex, lastVisibleIndex],
  );

  const rowCount = usePlannerRowCount({ org: currentOrg, allSongLists, dates: weeklyDates });

  const getColumnRangeForColumn = useCallback(
    (rowIndex: number, column: number): { columnStart: number; columnCount: number } => {
      // a church row is always one column: a repeating row's run selects one Sunday at a time (issue #4191)
      const rowSchema = schema?.rows.find((row) => row.row === rowIndex);
      let result: { columnStart: number; columnCount: number } | undefined;
      if (rowSchema?.intervalType === IntervalType.Month) {
        result = columnsInMonth
          .values()
          .find(({ columnStart, columnCount }) => column >= columnStart && column < columnStart + columnCount);
      }
      if (!result && rowSchema?.intervalType === IntervalType.Custom) {
        result = termHeaderInfo.find(
          ({ columnStart, columnCount }) => column >= columnStart && column < columnStart + columnCount,
        ) ?? { columnStart: column, columnCount: 1 };
      }
      return result ?? { columnStart: column, columnCount: 1 };
    },
    [columnsInMonth, schema?.rows, termHeaderInfo],
  );

  const nextColumn = useCallback(
    (row: number, column: number) => {
      const { columnStart, columnCount } = getColumnRangeForColumn(row, column);
      return (columnStart + columnCount) % weeklyDates.length;
    },
    [getColumnRangeForColumn, weeklyDates.length],
  );
  const prevColumn = useCallback(
    (row: number, column: number) => {
      const { columnStart } = getColumnRangeForColumn(row, column);
      return (columnStart - 1 + weeklyDates.length) % weeklyDates.length;
    },
    [getColumnRangeForColumn, weeklyDates.length],
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // a cell is committed on Enter or blur, and a deploy must not reload the page under an open cell menu, verses picker
  // or song selector either (#3265, #4169)
  useHoldAppReload(isEditing || isDialogOpen);

  const { moveDown, onKeyDown, selectedCell, setSelectedCell } = usePlannerKeyboardNavigation({
    defaultColumn: weeklyDates.indexOf(currentWeekDate) !== -1 ? weeklyDates.indexOf(currentWeekDate) : 0,
    rowCount,
    nextColumn,
    prevColumn,
    ignoreKeystrokes: isDialogOpen,
    columnCount: weeklyDates.length,
  });
  const selectedCellDate = weeklyDates[selectedCell.column];

  // the Sundays an edit of the selected cell shows through, when it is in a run row: from the selected Sunday to the
  // next one that holds something in a Same-every-Sunday row (issue #4191), to the month's last Sunday in a
  // Same-all-month row (issue #4210), and the selected Sunday alone when it has passed or lies before the row's
  // repeatFrom (getSelectionReach). By Sunday of the month and Does-not-repeat rows show the cell's own focus border.
  const selectionReach = useMemo(() => {
    if (selectedColumnIndex !== undefined) {
      return undefined;
    }
    const rowSchema = schema?.rows.find(({ row }) => row === selectedCell.row);
    const kind = getRepeatKind(rowSchema);
    if (!isRepeatingRow(currentOrg, rowSchema) || !isRunKind(kind)) {
      return undefined;
    }
    return getSelectionReach({
      kind,
      repeatFrom: getRepeatFrom(rowSchema),
      rowIndex: selectedCell.row,
      column: selectedCell.column,
      songLists,
      weeklyDates,
      currentWeekDate,
    });
  }, [currentOrg, currentWeekDate, schema?.rows, selectedCell, selectedColumnIndex, songLists, weeklyDates]);

  // the column of a selection not scrolled into view: a cell's right-click or long-press selects the cell under the
  // pointer, and scrolling it would move it away from its menu, which a scroll closes (#4169)
  const columnNotScrolledIntoViewRef = useRef<number>(undefined);

  // Scroll to keep the selected cell visible when navigating via keyboard
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || columnNotScrolledIntoViewRef.current === selectedCell.column) {
      return;
    }
    const scrollLeft = getScrollLeftToShowColumn({
      column: selectedCell.column,
      columnWidth,
      labelColumnWidth,
      scrollLeft: container.scrollLeft,
      viewWidth: container.clientWidth,
    });
    if (scrollLeft !== undefined) {
      container.scrollLeft = scrollLeft;
    }
  }, [selectedCell.column, columnWidth, labelColumnWidth]);

  // the selection not scrolled into view is this one only: a right-click in the column already selected moves no
  // column, and the mark left set would skip the scroll of a later columnWidth change
  useEffect(() => {
    columnNotScrolledIntoViewRef.current = undefined;
  }, [selectedCell]);

  useEffect(() => {
    if (selectedColumnIndex !== undefined) {
      setSelectedCell({ row: 1, column: selectedColumnIndex });
    }
  }, [selectedColumnIndex, setSelectedCell]);

  const setSelectedCellWrapper = useCallback(
    (value: CellPosition, { isScrolledIntoView = true }: SelectCellOptions = {}) => {
      columnNotScrolledIntoViewRef.current = isScrolledIntoView ? undefined : value.column;
      setSelectedColumnIndex(undefined);
      setSelectedCell(value);
    },
    [setSelectedCell],
  );

  function getHymnOfTheMonth(
    songList: HymnOfTheMonthSongList | undefined,
    date: DateString,
    columnStart: number,
    columnCount: number,
  ) {
    return (
      <SongCell
        allSongLists={allSongLists}
        key={`${songList?.id ?? date}`}
        customMusic={customMusic}
        date={date}
        hymnals={hymnals}
        row={1}
        label={`${getMonthName(date)} Hymn of the Month`}
        songList={
          songList ?? {
            date,
            type: SongListType.HymnOfTheMonth,
            interval: { type: IntervalType.Month, start: date, end: getLastDayOfMonth(date) },
            songs: [],
          }
        }
        songListType={SongListType.HymnOfTheMonth}
        songListEntryType={SongListEntryType.Song}
        isChurch={isChurch}
        isInternalUser={isInternalUser}
        isOrgAdmin={isOrgAdmin}
        columnStart={columnStart}
        columnCount={columnCount}
      />
    );
  }

  const getCellValues = useCallback((row: string): string[] => {
    return row.split('\t');
  }, []);

  const getCopiedRows = useCallback(
    (text: string): string[] => {
      const rows = text.split('\n');
      if (selectedColumnIndex === undefined || rows.length !== 1) {
        return rows;
      }
      // special case - (single row copy + column selection paste) => convert columns to rows for paste
      return getCellValues(rows[0]);
    },
    [getCellValues, selectedColumnIndex],
  );

  // pastes clipboard text (cells separated by tabs, rows by newlines) starting at the selected cell
  const pasteText = useCallback(
    (text: string) => {
      if (!selectedCell) {
        return;
      }
      // every pasted Sunday's weekly list, a repeating row's included: the fill shows it on the Sundays after (#4191).
      // In a church's Same-all-month row a pasted entry also covers the rest of its month as an edit does, so the later
      // Sundays the paste does not write at the row lose their own entry there (getPasteWrites, #4210)
      const pastedCells: { row: number; date: DateString; entry: SongListEntry }[] = [];
      const rows = getCopiedRows(text);
      for (const [rowIndex, rowValue] of rows.entries()) {
        const cells = getCellValues(rowValue);
        for (const [cellIndex, cellValue] of cells.entries()) {
          const date = addDayOffset(selectedCellDate, 7 * cellIndex);
          const row = selectedCell.row + rowIndex;
          const schemaRow = schema?.rows?.find(({ row: currentRow }) => currentRow === row);
          // the song and its verses when the text ends in a 'vv.' suffix (issue #4169)
          const entry = entryFromPastedText({
            row,
            text: cellValue,
            entryType: schemaRow?.type ?? SongListEntryType.Song,
            hymnals,
            customHymns: currentOrg && customMusic[currentOrg?.id],
            isInternalUser,
            orgType: currentOrg?.type,
          });
          if (entry) {
            pastedCells.push({ row, date, entry });
          }
        }
      }
      void applyPaste(plannerWriteContext, { rows: schema?.rows ?? [], cells: pastedCells });
    },
    [
      currentOrg,
      customMusic,
      getCellValues,
      getCopiedRows,
      hymnals,
      isInternalUser,
      plannerWriteContext,
      schema?.rows,
      selectedCell,
      selectedCellDate,
    ],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      console.log('onPaste');
      if (isEditing) {
        return; // Allow currently open editor to handle paste
      }
      const text = event.clipboardData?.getData('text/plain').replace(/\r/g, ''); // strip Microsoft cruft
      if (text && selectedCell) {
        pasteText(text);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isEditing, pasteText, selectedCell],
  );

  // Paste from a cell's context menu, which has no paste event to read
  const pasteFromClipboard = useCallback(async () => {
    try {
      const text = (await navigator.clipboard.readText()).replace(/\r/g, '');
      if (text) {
        pasteText(text);
      }
    } catch (error) {
      // permission denied
      console.log(`Paste from the clipboard failed: ${String(error)}`);
    }
  }, [pasteText]);

  const onPasteCell = useMemo(
    () => (canReadClipboard ? () => void pasteFromClipboard() : undefined),
    [pasteFromClipboard],
  );

  const onCopy = useCallback(() => {
    const date = selectedColumnDate ?? selectedCellDate;
    if (!date || !currentOrg) {
      return;
    }
    let text: string | undefined;
    const songLists = allSongLists.filter(
      (list) => list.type !== SongListType.Interval || dateRangeOverlapsDate(list.interval, date),
    );
    // a church's liturgy rows only: the hymns of the month all carry row 1 and would be copied as that row (issue #4170)
    const songEntries =
      currentOrg.type === OrgType.School
        ? getSongEntriesForDate(currentOrg, date, songLists, Object.values(SongListEntryType))
        : getWeeklySongEntriesForDate(currentOrg, date, songLists, { today, now: getReaderNow(), writtenThrough });
    if (selectedColumnDate) {
      text = '';
      let firstEntry = true;
      const maxRow = Math.max(...(songEntries.map(({ row }) => row) ?? []), 0);
      for (let currentRow = 1; currentRow <= maxRow; currentRow++) {
        const entry = songEntries.find(({ row }) => row === currentRow);
        if (!firstEntry) {
          text += '\n';
        }
        text += getEntryText(entry) ?? '';
        firstEntry = false;
      }
    } else if (selectedCell) {
      const entry = songEntries?.find(({ row }) => row === selectedCell.row);
      text = getEntryText(entry);
    }
    if (text) {
      void navigator.clipboard.writeText(text);
    }

    // a song followed by its verses ('517 - All Hail the Power of Jesus' Name!  vv. 1–3, 6'), as the cell reads
    function getEntryText(entry: SongListEntry | undefined): string | undefined {
      if (!isBound(entry)) {
        return entry?.text;
      }
      const songName = getDisplayNameForSongListEntry({ entry: entry, hymnals, customMusic });
      return songName && appendVersesSuffix(songName, entry.verses);
    }
  }, [
    allSongLists,
    currentOrg,
    customMusic,
    getReaderNow,
    hymnals,
    selectedCell,
    selectedCellDate,
    selectedColumnDate,
    today,
    writtenThrough,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener('paste', onPaste, { signal: controller.signal });
    window.addEventListener('copy', onCopy, { signal: controller.signal });
    return () => controller.abort();
  }, [onPaste, onCopy]);

  const getSongCell = useCallback(
    ({
      rowIndex,
      songList,
      date,
      columnStart,
      columnCount,
      songListType,
      tag,
      tagOutlined,
      isInherited,
      title,
      blankMarks,
      isRun,
      updateEntry,
      clearLabelAt,
      cellKey,
    }: {
      rowIndex: number;
      songList: SongList;
      date: string;
      columnStart: number;
      columnCount: number;
      songListType: SongListType;
      tag?: string;
      tagOutlined?: boolean;
      isInherited?: boolean;
      title?: string;
      blankMarks?: RepeatingRowBlankMark[];
      isRun?: boolean;
      updateEntry?: (entry: SongListEntry | undefined, date: DateString) => void;
      clearLabelAt?: (date: DateString) => ClearLabel | undefined;
      // the React key, when the cell's own list and date do not make a stable one
      cellKey?: string;
    }) => {
      const row = schema?.rows.find(({ row: current }) => current === rowIndex);
      const isMobile = getPlatform() === Platform.Mobile;
      const label = isMobile ? row?.label : undefined;
      // a phone shows a repeating row's glyphs in the cell, with or without a label (issue #4170)
      const isRepeating = isRepeatingRow(currentOrg, row);
      return (
        <SongCell
          allSongLists={allSongLists}
          key={cellKey ?? `${songList?.id ?? date}-${rowIndex}`}
          customMusic={customMusic}
          date={date}
          hymnals={hymnals}
          label={label}
          labelGlyphs={isMobile && isRepeating ? <RepeatGlyphs row={row} /> : undefined}
          tag={tag}
          tagOutlined={tagOutlined}
          isInherited={isInherited}
          title={title}
          blankMarks={blankMarks}
          isRun={isRun}
          updateEntry={updateEntry}
          clearLabelAt={clearLabelAt}
          songList={songList}
          songListType={songListType}
          songListEntryType={row?.type ?? SongListEntryType.Song}
          isChurch={isChurch}
          selectedCell={selectedColumnIndex !== undefined ? undefined : selectedCell}
          setSelectedCell={setSelectedCellWrapper}
          setIsDialogOpen={setIsDialogOpen}
          setIsEditing={setIsEditing}
          moveDown={moveDown}
          isInternalUser={isInternalUser}
          isOrgAdmin={isOrgAdmin}
          row={rowIndex}
          columnStart={columnStart}
          columnCount={columnCount}
          onCopyCell={onCopy}
          onPasteCell={onPasteCell}
        />
      );
    },
    [
      schema?.rows,
      allSongLists,
      currentOrg,
      customMusic,
      hymnals,
      isChurch,
      selectedColumnIndex,
      selectedCell,
      setSelectedCellWrapper,
      moveDown,
      isInternalUser,
      isOrgAdmin,
      onCopy,
      onPasteCell,
    ],
  );

  const onLocalKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isClearKey(event.key)) {
        // only the column's own weekly list: matching on start date alone cleared that month's hymn of the
        // month instead whenever the Sunday fell on the 1st (issue #3609); from the current Sunday on, it toggles as a
        // cell's Clear does: a Sunday holding anything is emptied, and one holding nothing gets a blank in every
        // repeating row that shows something there, which stops their fill so the Sunday shows nothing (issue #4200).
        // So only the press clears, not a held key's repeats
        if (event.repeat || selectedColumnIndex === undefined || !selectedColumnDate) {
          return;
        }
        void applyColumnClear(plannerWriteContext, { schema, date: selectedColumnDate });
      }
    },
    [plannerWriteContext, schema, selectedColumnDate, selectedColumnIndex],
  );

  useEffect(() => {
    if (selectedColumnIndex !== undefined) {
      selectedWeekRef.current?.focus();
    }
  }, [selectedColumnIndex, selectedWeekRef]);

  const headerContent = useCallback(
    (title: ReactElement) => (
      <HeaderContent>
        <BackButton key="back" onClick={onBack} />
        <BackButtonPlaceholder />
        {title}
        {allSongLists.containsUpdates && <SyncAnimation $size="30px" />}
      </HeaderContent>
    ),
    [allSongLists.containsUpdates, onBack],
  );

  const dateHeaders = [...weeklyDates.entries()]
    .filter(([index]) => isColumnVisible(index))
    .map(([index, date]) => (
      <DateHeader
        key={date}
        id={getDayElementId(date)}
        highlight={date === currentWeekDate}
        columnIndex={index}
        setSelectedColumnIndex={setSelectedColumnIndex}
        isOutsideSchoolTerms={datesOutsideSchoolTerm.has(date)}
      >
        {titleFromDate(currentOrg, date, schoolTerms)}
        {currentOrg && <DownloadSongSheetsButton org={currentOrg} date={date} />}
      </DateHeader>
    ));

  const hasHymnOfTheMonth = currentOrg?.type !== OrgType.School;

  const gridCells = useMemo(
    () =>
      [...Array(rowCount).keys()]
        .map((index) => index + 1)
        .map((rowIndex) => {
          const rowSchema = schema?.rows.find(({ row }) => row === rowIndex);
          const intervalType = rowSchema?.intervalType;
          const songListType = intervalType ? SongListType.Interval : getWeeklySongListType(currentOrg);
          // a church's plain weekly cell writes through the planner, as a repeating row's does, so an edit made while a
          // passed Sunday is not yet written down writes it down first (issue #4209); a school's or small group's
          // writes its own week's list
          const getWeeklyCell = (columnIndex: number, weeklyDate: DateString) =>
            getSongCell({
              rowIndex,
              songList: weeklySongLists.get(weeklyDate) ?? getEmptyWeeklySongList(currentOrg, weeklyDate),
              date: weeklyDate,
              columnStart: columnIndex,
              columnCount: 1,
              songListType,
              ...(isChurch && { updateEntry: (entry, date) => updateCellEntry({ rowIndex, date, entry }) }),
            });

          // a church row that repeats (issue #4191): its cells filled from the row's own Sundays, each edit writing
          // the Sunday edited (and in a Same-all-month row the rest of its month, updateRepeatingRowEntry)
          const cells = repeatingRowCells.get(rowIndex);
          if (cells) {
            return cells
              .filter(({ columnStart, columnCount }) => isColumnRangeVisible(columnStart, columnCount))
              .map((cell) =>
                getSongCell({
                  rowIndex,
                  songList: cell.displayList,
                  date: cell.date,
                  columnStart: cell.columnStart,
                  columnCount: cell.columnCount,
                  songListType: getWeeklySongListType(currentOrg),
                  tag: cell.tag,
                  tagOutlined: cell.tagOutlined,
                  isInherited: cell.isInherited,
                  title: cell.title,
                  blankMarks: cell.blankMarks,
                  isRun: cell.isRun,
                  updateEntry: (entry, date) => updateCellEntry({ rowIndex, date, entry }),
                  clearLabelAt: (date) => getRepeatingCellClearLabel(rowIndex, date, cell.entry),
                  // keyed by its first Sunday, not that Sunday's list: a list created there (another row's edit,
                  // another device) does not remount the cell and lose an editor open on another of its Sundays
                  cellKey: `${cell.date}-${rowIndex}`,
                }),
              );
          }

          if (songListType === SongListType.Interval) {
            // termly songs
            if (intervalType === IntervalType.Custom) {
              return termHeaderInfo
                .filter(({ term, columnStart, columnCount }) => term && isColumnRangeVisible(columnStart, columnCount))
                .map(({ startDate, term, columnStart, columnCount }) =>
                  getSongCell({
                    rowIndex,
                    songList: term as IntervalSongList,
                    date: startDate,
                    columnStart,
                    columnCount,
                    songListType,
                  }),
                );
            }
            if (intervalType === IntervalType.Month) {
              return [...columnsInMonth]
                .map(([firstDayOfMonth, { columnStart, columnCount }]) => ({
                  firstDayOfMonth,
                  columnStart,
                  columnCount,
                  interval: {
                    type: IntervalType.Month,
                    start: firstDayOfMonth,
                    end: getLastDayOfMonth(firstDayOfMonth),
                  },
                }))
                .filter(
                  ({ interval, columnStart, columnCount }) =>
                    getTermForDateRange(interval, schoolTerms) && isColumnRangeVisible(columnStart, columnCount),
                )
                .map(({ firstDayOfMonth, columnStart, columnCount, interval }) => {
                  return getSongCell({
                    rowIndex,
                    songList: monthlySongLists.get(firstDayOfMonth) ?? {
                      type: SongListType.Interval,
                      interval,
                      songs: [],
                    },
                    date: firstDayOfMonth,
                    columnStart,
                    columnCount,
                    songListType,
                  });
                });
            }
          }

          return [...weeklyDates.entries()]
            .filter(
              ([columnIndex, weeklyDate]) => !datesOutsideSchoolTerm.has(weeklyDate) && isColumnVisible(columnIndex),
            )
            .map(([columnIndex, weeklyDate]) => getWeeklyCell(columnIndex, weeklyDate));
        }),
    [
      currentOrg,
      columnsInMonth,
      datesOutsideSchoolTerm,
      getSongCell,
      isChurch,
      isColumnRangeVisible,
      isColumnVisible,
      getRepeatingCellClearLabel,
      monthlySongLists,
      repeatingRowCells,
      rowCount,
      schema?.rows,
      schoolTerms,
      termHeaderInfo,
      updateCellEntry,
      weeklyDates,
      weeklySongLists,
    ],
  );

  const disabledWeeks = useMemo(() => {
    if (currentOrg?.type !== OrgType.School) {
      return null;
    }
    return [...weeklyDates.entries()]
      .filter(([index, weeklyDate]) => datesOutsideSchoolTerm.has(weeklyDate) && isColumnVisible(index))
      .map(([columnIndex, weeklyDate]) => (
        <DisabledColumn
          key={weeklyDate}
          date={weeklyDate}
          columnIndex={columnIndex}
          rowCount={rowCount}
          allSongLists={allSongLists}
        />
      ));
  }, [allSongLists, currentOrg?.type, datesOutsideSchoolTerm, isColumnVisible, rowCount, weeklyDates]);

  const onSelectTerm = useCallback(
    (term: IntervalSongList) => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const termDate = getWeeklyDateForOrganization(currentOrg, term.interval.start);
      const columnIndex = weeklyDates.indexOf(termDate);
      if (columnIndex !== -1) {
        container.scrollTo({
          left: getScrollLeftToCenterColumn({
            column: columnIndex,
            columnWidth,
            labelColumnWidth,
            viewWidth: container.clientWidth,
          }),
          behavior: 'smooth',
        });
      }
    },
    [columnWidth, currentOrg, labelColumnWidth, weeklyDates],
  );

  const headers: Array<{ key: string; columnCount: number; content: ReactElement }> = useMemo(() => {
    return currentOrg?.type === OrgType.School
      ? termHeaderInfo.map(({ startDate, term, columnCount }) => ({
          key: startDate,
          columnCount,
          content:
            term || termHeaderInfo.length === 1 ? (
              headerContent(<TermSelector term={term} onSelectTerm={onSelectTerm} />)
            ) : (
              <div />
            ),
        }))
      : Object.entries(columnsInYear).map(([year, columnCount]) => ({
          key: year,
          columnCount,
          content: headerContent(<YearHeader key="header">{`${year} Liturgy Planner`}</YearHeader>),
        }));
  }, [currentOrg?.type, columnsInYear, headerContent, onSelectTerm, termHeaderInfo]);

  const onOpenDashboard = useCallback(async () => {
    await navigate(getUrlForPage(Pages.Dashboard));
  }, [navigate]);

  return (
    <>
      <OuterPageContent key="outer">
        <InnerPageContent key="inner" ref={scrollContainerRef} onScroll={onScroll}>
          <PlannerSchemaWrapper key="planner-schema-wrapper">
            {isLabelColumnRendered && (
              <PlannerSchemaColumn key="planner-schema-column" allSongLists={allSongLists} songRowCount={rowCount} />
            )}
            <PlannerDataWrapper>
              <TitleStacker key="year-term-stacker">
                {headers.map(({ key, columnCount, content }) => (
                  <TitleWrapper key={key} $columnCount={columnCount}>
                    <TitleHorizontalWrapper>
                      <TitleInnerWrapper>{content}</TitleInnerWrapper>
                    </TitleHorizontalWrapper>
                  </TitleWrapper>
                ))}
                <PlannerButtonBar>
                  {getPlatform() !== Platform.Mobile && <EditTermsButton styled={true} />}
                  <UploadSongButton />
                  {(isInternalUser || isOrgAdmin) && <DashboardButton onClick={onOpenDashboard} />}
                </PlannerButtonBar>
              </TitleStacker>
              <DateHeaders key={'date-headers'} $totalColumns={weeklyDates.length}>
                {dateHeaders}
              </DateHeaders>
              {currentOrg?.type !== OrgType.School && (
                <RowWrapper
                  key={'hymn-of-the-month-row'}
                  $height={SongRowHeightDouble}
                  $totalColumns={weeklyDates.length}
                >
                  {[...columnsInMonth]
                    .filter(([_firstDay, { columnStart, columnCount }]) =>
                      isColumnRangeVisible(columnStart, columnCount),
                    )
                    .map(([firstDayOfMonth, { columnStart, columnCount }]) =>
                      getHymnOfTheMonth(
                        hymnsOfTheMonth.get(firstDayOfMonth),
                        firstDayOfMonth,
                        columnStart,
                        columnCount,
                      ),
                    )}
                </RowWrapper>
              )}
              <PlannerGrid
                $rows={rowCount}
                $cols={weeklyDates.length}
                $hasHymnOfTheMonth={hasHymnOfTheMonth}
                $rowHeight={getPlatform() === Platform.Mobile ? SongRowHeightDouble : SongRowHeightSingle}
                onKeyDown={onKeyDown}
              >
                {disabledWeeks}
                {gridCells}
                {selectionReach && (
                  <SelectionReachOutline
                    key="selection-reach"
                    style={{
                      gridRow: selectedCell.row,
                      gridColumnStart: selectionReach.columnStart + 1,
                      gridColumnEnd: selectionReach.columnStart + selectionReach.columnCount + 1,
                    }}
                  />
                )}
                {selectedColumnIndex !== undefined && selectedColumnIndex > 0 && (
                  <SelectedColumnOutline
                    key={`select-column`}
                    tabIndex={0}
                    onKeyDown={onLocalKeyDown}
                    ref={selectedWeekRef}
                    style={{ gridRowStart: 1, gridRowEnd: rowCount + 1, gridColumn: selectedColumnIndex + 1 }}
                  />
                )}
              </PlannerGrid>
            </PlannerDataWrapper>
          </PlannerSchemaWrapper>
        </InnerPageContent>
        {/* below the scroll container, not above it: the button bar is fixed to the top of the window, and
            the sticky headers stick to the scroll container, which only gets shorter */}
        <PlannerSaveRefusalBanner />
      </OuterPageContent>
    </>
  );
};

const PlannerButtonBar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: ${PlannerYearHeight};
  padding: 0 15px;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  gap: 5px;
`;

function DashboardButton({ onClick }: { onClick: () => void }) {
  return (
    <div>
      <DashboardButtonText onClick={onClick}>Dashboard</DashboardButtonText>
      <DashboardIconWrapper onClick={onClick} />
    </div>
  );
}

const DashboardButtonText = styled.button`
  color: var(--color-text);
  font-family: Jost-SemiBold, Arial, sans-serif;
  border: 3px solid var(--color-text);
  border-radius: ${HeaderButtonBorderRadius};
  padding: ${HeaderButtonPaddingDesktop};
  cursor: pointer;
  &:active {
    color: var(--color-background);
    background-color: var(--color-text);
  }
  @media (max-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const DashboardIconWrapper = styled(DashboardIcon)`
  width: 30px;
  height: 30px;
  fill: var(--color-text);
  cursor: pointer;
  @media (min-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const RowWrapper = styled.div<{ $height: string; $totalColumns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$totalColumns}, ${SongRowWidth});
  grid-template-rows: ${(props) => props.$height};
  height: ${(props) => props.$height};
  text-align: left;
`;

const PlannerGrid = styled.div<{ $rows: number; $cols: number; $hasHymnOfTheMonth: boolean; $rowHeight: string }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols}, ${SongRowWidth});
  grid-template-rows: repeat(${(props) => props.$rows}, ${(props) => props.$rowHeight});
  text-align: left;
`;

function titleFromDate(church: Org | undefined, date: DateString, schoolTerms: IntervalSongList[]) {
  const shortDate = `${getDateAsShortString(date)}`;

  if (church?.type !== OrgType.School) {
    const day = Date.fromString(date);
    // FIXME(lukesneeringer): Put in a smarter way to think about "The" prefixes.
    const liturgicalLabel = day.label('#F', '#-O #-D #P #S').replace(/^The (?=[BNRS])/, '');
    return day?.format(`%b %-d: ${liturgicalLabel}`);
  }

  const week: DateRange = { start: date, end: addDayOffset(date, 6) };
  const term = getTermForDateRange(week, schoolTerms);
  if (!term) {
    return shortDate;
  }
  return `Week ${getTermWeekNumber(term, week)} - ${shortDate}`;
}

const InnerPageContent = styled.div`
  flex: auto;
  overflow: scroll;
  overscroll-behavior-x: none; /* avoid swipe to go back */
  overscroll-behavior-y: none;
  max-width: 100vw;
  /* check out scroll-snap-points */
  /*scroll-snap-points-y: repeat(100vh);*/
  /*scroll-snap-type: y mandatory;*/
  /*scroll-snap-align: start; - on child */
`;

const TitleStacker = styled.div`
  display: flex;
  width: fit-content;
  text-align: left;
  z-index: 6;
  position: sticky;
  top: 0;
`;

const TitleWrapper = styled.div<{ $columnCount: number }>`
  display: flex;
  flex-direction: column;
  width: calc(${(props) => props.$columnCount} * ${SongRowWidth});
`;

const TitleHorizontalWrapper = styled.div`
  background-color: var(--color-background);
  height: ${PlannerYearHeight};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: ${PlannerHeaderButtonGap};
  background-color: var(--color-background);
`;

const TitleInnerWrapper = styled.div`
  position: sticky;
  left: 0;
  width: fit-content;
  height: ${PlannerYearHeight};
  display: flex;
  align-items: center;
`;

const DateHeaders = styled.div<{ $totalColumns: number }>`
  z-index: 4;
  position: sticky;
  top: ${DateRowTop};
  width: calc(${(props) => props.$totalColumns} * ${SongRowWidth});
  height: ${SongRowHeightSingle};
`;

function DateHeader({
  highlight,
  id,
  children,
  setSelectedColumnIndex,
  isOutsideSchoolTerms,
  columnIndex,
}: {
  highlight: boolean;
  id: string;
  setSelectedColumnIndex: (index: number | undefined) => void;
  isOutsideSchoolTerms: boolean;
  children: React.ReactNode;
  columnIndex: number;
}) {
  const onClick = useCallback(() => {
    if (!isOutsideSchoolTerms) {
      setSelectedColumnIndex(columnIndex);
    }
  }, [columnIndex, isOutsideSchoolTerms, setSelectedColumnIndex]);
  return (
    <DateHeaderWrapper
      id={id}
      $highlight={highlight}
      $isOutsideSchoolTerms={isOutsideSchoolTerms}
      $columnIndex={columnIndex}
      onClick={onClick}
    >
      {children}
    </DateHeaderWrapper>
  );
}

const DateHeaderWrapper = styled.div<{ $highlight: boolean; $isOutsideSchoolTerms: boolean; $columnIndex: number }>`
  position: absolute;
  left: calc(${(props) => props.$columnIndex} * ${SongRowWidth});
  width: ${SongRowWidth};
  height: ${SongRowHeightSingle};
  font-family: Jost-SemiBold, Arial, sans-serif;
  outline: 1px solid var(--color-text); /* outline instead of border takes up 0 space */
  padding: 0 10px;
  color: ${(props) => (props.$highlight ? 'var(--color-date-highlight-text)' : 'var(--color-text)')};
  background-color: var(--color-planner-row-header);
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props) => (props.$isOutsideSchoolTerms ? '' : 'cursor: pointer;')}
`;

const SelectedColumnOutline = styled.div`
  z-index: 1;
  width: ${SongRowWidth};
  height: 100%;
  outline: ${CellHoverOutlineWidth} solid var(--color-text);
  pointer-events: none;
`;

// the reach of the selected Sunday of a repeating row's run (issue #4191): above a focused cell (z-index 2), below the
// sticky headers (4 and up)
const SelectionReachOutline = styled.div`
  z-index: 3;
  outline: ${CellHoverOutlineWidth} solid var(--color-text);
  pointer-events: none;
`;

function DisabledColumn({
  date,
  rowCount,
  columnIndex,
  allSongLists,
}: {
  date: DateString;
  rowCount: number;
  columnIndex: number;
  allSongLists: AllSongLists;
}) {
  const onMouseDownDisabled = useCallback(() => {
    void (async () => {
      if (await confirm({ confirmation: <span>Would you like to create a term?</span> })) {
        const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
        const overlappingTerm = getTermForDateRange({ start: date, end: date }, schoolTerms);
        const start = overlappingTerm ? addDayOffset(overlappingTerm.interval.start, 1) : date;
        const end = addDayOffset(start, 6);
        await allSongLists.addCustomInterval({ start, end });
        await openTermPlanner({ _unused: 'hi' });
      }
    })();
  }, [allSongLists, date]);

  return (
    <DisabledColumnWrapper
      key={`disabled-column-${date}`}
      style={{ gridRowStart: 1, gridRowEnd: rowCount + 1, gridColumn: columnIndex + 1 }}
      onClick={onMouseDownDisabled}
    />
  );
}

const DisabledColumnWrapper = styled.div`
  background-color: var(--color-background-button-disabled);
`;

const PlannerSchemaWrapper = styled.div`
  display: flex;
  width: max-content;
`;

const PlannerDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

function usePlannerRowCount({
  org,
  allSongLists,
  dates,
}: {
  org: Org | undefined;
  allSongLists: AllSongLists;
  dates: NonEmptyArray<DateString>;
}): number {
  const maxSongListRow = useMemo(
    () =>
      Math.max(
        ...allSongLists
          .filter(
            (list) => getSongListStartDate(list) >= dates[0] && getSongListStartDate(list) <= dates[dates.length - 1],
          )
          .flatMap(({ songs }) => songs)
          .map(({ row }) => row),
      ),
    [allSongLists, dates],
  );
  if (org?.type === OrgType.School) {
    const schema = getSchoolSongListSchema(org, allSongLists);
    return schema?.rows.length;
  } else {
    const maxPlannerRow = Math.max(...(org?.churchLiturgySchema?.rows.map(({ row }) => row) ?? []), 0);
    return Math.max(MinSongListRowCount, maxSongListRow + 1, maxPlannerRow + 1);
  }
}

function getOldestPlannerDate(
  church: ClientOrg | undefined,
  schoolTerms: IntervalSongList[],
  allSongLists: AllSongLists,
  currentWeekDate: string,
) {
  let startDate: DateString;
  if (church?.type === OrgType.School) {
    const firstTerm = schoolTerms?.length ? schoolTerms[0] : undefined;
    const firstTermStart = firstTerm?.interval.start ?? todayDate();
    startDate = maxDate([minSchoolStartDate, addDayOffset(firstTermStart, -schoolPlannerBeginEndPaddingWeeks * 7)]);
  } else {
    const earliestSongListDate = minDate([...allSongLists.map((list) => getSongListStartDate(list)), currentWeekDate]);
    startDate = earliestSongListDate;
  }
  return getWeeklyDateForOrganization(church, startDate);
}

function getNewestPlannerDate(
  church: ClientOrg | undefined,
  schoolTerms: IntervalSongList[],
  allSongLists: AllSongLists,
  currentWeekDate: string,
) {
  let endDate: DateString;
  if (church?.type === OrgType.School) {
    const lastTerm = schoolTerms?.length ? schoolTerms[schoolTerms.length - 1] : undefined;
    const lastTermEnd = lastTerm?.interval.end ?? todayDate();
    endDate = minDate([maxSchoolEndDate, addDayOffset(lastTermEnd, schoolPlannerBeginEndPaddingWeeks * 7)]);
  } else {
    // a list ending at newestDate (an open-ended Fixed list a stale #4170 bundle may still write, or one cached
    // before the server's sweep, issue #4191) would stretch the planner to the year 3000, so it is left out
    const newestListDate = maxDate([
      ...allSongLists.map((list) => getSongListEndDate(list)).filter((endDate) => endDate < newestDate),
      currentWeekDate,
    ]);
    endDate = addDayOffset(newestListDate, songFutureSizeInWeeks * 7);
  }
  return getWeeklyDateForOrganization(church, endDate);
}
